<!--
	Signs and Plaques body of the Interventions legend; toggling a sign also toggles its speed
	plaque and halo.
-->
<script lang="ts">
	import LegendIcon from '#lib/components/LegendIcon.svelte';
	import DataNotAvailable from '#lib/components/DataNotAvailable.svelte';
	import LegendItems from '../../legends/LegendItems.svelte';
	import { legendStore } from '#lib/stores/legendStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { mapExtentStore } from '#lib/stores/mapExtentStore.svelte.js';
	import { EInterventions, ESafeCurvesModule, type ISafeCurvesSystemLayers } from '../interfaces';
	import { disableByZoom } from '../../legendHelpers';

	const systemData = $derived(geodataStore.systemData as ISafeCurvesSystemLayers);

	// Available when getVectorLayerSource() emitted a non-null Advisory Signs source.
	const signsAvailable = $derived(
		systemData[ESafeCurvesModule.Interventions]?.[EInterventions.CurveAdvisorySigns] != null
	);

	const signsLegend = $derived(
		legendStore.layers.find((l) => l.systemId === EInterventions.CurveAdvisorySigns)
	);
	const speedsLegend = $derived(
		legendStore.layers.find((l) => l.systemId === EInterventions.CurveAdvisorySpeeds)
	);
	const halosLegend = $derived(
		legendStore.layers.find((l) => l.systemId === EInterventions.CurveAdvisorySignsHalo)
	);

	const mapZoom = $derived(mapExtentStore.zoom);

	// Keep a sibling legend layer (speed plaques, sign halos) in step with a sign item by label.
	function setSiblingVisibility(sibling: typeof speedsLegend, enabled: boolean, label: string) {
		if (!sibling) return;
		const item = sibling.legendItems.find((i) => i.label === label);
		if (item) {
			legendStore.setLegendLayerVisibility({
				layerId: sibling.layerId,
				legendItemId: item.legendItemId,
				enabled
			});
		}
	}

	function toggleSign(legendItemId: string, enabled: boolean) {
		const item = signsLegend?.legendItems.find((i) => i.legendItemId === legendItemId);
		if (!signsLegend || !item) return;
		legendStore.setLegendLayerVisibility({ layerId: signsLegend.layerId, legendItemId, enabled });
		setSiblingVisibility(speedsLegend, enabled, item.label);
		setSiblingVisibility(halosLegend, enabled, item.label);
	}

	function setAll(enabled: boolean) {
		if (!signsLegend || !speedsLegend) return;
		legendStore.setAllLegendLayerVisibility({ layerId: signsLegend.layerId, enabled });
		signsLegend.legendItems.forEach((item) => {
			setSiblingVisibility(speedsLegend, enabled, item.label);
			setSiblingVisibility(halosLegend, enabled, item.label);
		});
	}
</script>

{#if !signsAvailable || !signsLegend || !speedsLegend}
	<DataNotAvailable />
{:else}
	<LegendItems
		legend={signsLegend}
		disabled={disableByZoom(signsLegend.minzoom, mapZoom)}
		ontoggle={toggleSign}
		ontoggleall={setAll}>
		{#snippet patch({ image })}
			<LegendIcon><img src={image} alt="" /></LegendIcon>
		{/snippet}
	</LegendItems>
{/if}
